import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';

function fmtPrice(n) {
  return n != null ? `$${Number(n).toLocaleString('en-US')}` : null;
}

// Map a BE item document onto the unified asset shape the saved
// UI renders against. The detail rows want year/make/model/miles/color;
// the mosaic wants `image`; the hero wants `price` + `auctionInHrs` —
// produce all of them so every consumer can pick.

function lightCodesFromDoc(doc) {
  const raw = doc?.SALELIGHTS;
  if (Array.isArray(raw) && raw.length) {
    return raw
      .map((entry) => {
        if (entry == null) return '';
        if (typeof entry === 'string') return entry.trim();
        const named = entry.NAME ?? entry.name;
        if (named != null && String(named).trim()) return String(named).trim();
        const color = entry.COLOR ?? entry.color;
        if (!color) return '';
        const map = { green: 'G', yellow: 'Y', red: 'R', white: 'W', blue: 'B' };
        const word = String(color).trim();
        return map[word.toLowerCase()] ?? word.charAt(0).toUpperCase();
      })
      .filter(Boolean);
  }
  if (Array.isArray(doc?.lights) && doc.lights.length) {
    return doc.lights.map((c) => String(c).trim()).filter(Boolean);
  }
  if (doc?.light != null && doc.light !== '') return [String(doc.light).trim()];
  return [];
}

function normalizeVehicleDoc(doc) {
  if (!doc) return null;
  const id = idOf(doc);
  if (!id) return null;
  const year = doc.YEAR ?? null;
  const make = doc.MAKE ?? null;
  const model = doc.MODEL ?? null;
  const trim = doc.SERIES ?? null;
  const image = Array.isArray(doc.IMAGES) && doc.IMAGES.length > 0
    ? doc.IMAGES[0].URL
    : null;
  const lights = lightCodesFromDoc(doc);
  return {
    id,
    year,
    make,
    model,
    trim,
    miles: doc.ODOMETERREADING ?? null,
    color: doc.EXTERIORCOLOR ?? null,
    // The real condition grade, carried beside miles and colour because the row
    // prints all three. Never derived: no grade on the record means no grade.
    grade: doc.CONDITIONREPORT?.GRADE ?? null,
    auction: doc.AUCTIONID ?? null,
    auctionInHrs: doc.auctionInHrs ?? null,
    price: doc.price ?? null,
    image,
    title: [year, make, model].filter(Boolean).join(' ') || doc.name || null,
    meta: null,
    lights,
    light: lights[0] ?? null,
  };
}

// Same normalization for assets that arrive from the search UI
// (ResultsPage card shape) rather than the BE /items endpoint.
function normalizeAssetCard(input) {
  if (!input?.id) return null;
  const titleParts = input.title ? String(input.title).split(' ') : [];
  const year = input.year ?? (Number(titleParts[0]) || null);
  const make = input.make ?? titleParts[1] ?? null;
  const model = input.model ?? (titleParts.slice(2).join(' ') || null);
  const lights = lightCodesFromDoc(input);
  return {
    id: input.id,
    year,
    make,
    model,
    trim: input.trim ?? null,
    miles: input.miles ?? null,
    color: input.color ?? null,
    grade: input.grade ?? null,
    auction: input.auction ?? null,
    auctionInHrs: input.auctionInHrs ?? null,
    price: input.price ?? null,
    image: input.image ?? null,
    title: input.title ?? [year, make, model].filter(Boolean).join(' ') ?? null,
    meta: input.meta ?? null,
    lights,
    light: lights[0] ?? input.light ?? null,
  };
}

function normalizeCategory(doc) {
  if (!doc) return null;
  const id = idOf(doc);
  if (!id) return null;
  return {
    id,
    name: doc.name ?? 'Untitled',
    slug: doc.slug ?? null,
    isBuiltIn: doc.is_built_in === true,
    description: doc.description ?? '',
    target: doc.target ?? null,
    // Wall-clock the BE stamps on every write — surface it so the FE can
    // sort by "most recent" without re-fetching.
    updatedAt: doc.updated_at ?? doc.created_at ?? null,
    createdAt: doc.created_at ?? null,
    assets: Array.isArray(doc.assets)
      ? doc.assets.map((v) => ({
          id: v.assetId,
          note: v.note ?? null,
          // Dealer planning worksheet fields — edited from the VDP.
          targetPrice: v.targetPrice ?? null,
          items: v.items ?? []
        }))
      : []
  };
}

export const likedStore = defineStore('likedStore', () => {
  const assetBank = ref({});
  const categories = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const loaded = ref(false);
  // Local UI state for the Saved tab's sort sheet — never sent to the
  // server. Drives `sortedCollections`.
  const sortMode = ref('recent'); // 'recent' | 'alphabetical' | 'most-assets'
  // Per-asset in-flight fetches so concurrent hydrations don't N+1
  // the BE — once a fetch is in flight for an id, the next call awaits
  // the same promise.
  const inflightAssetFetches = new Map();

  async function loadCategories({ force = false } = {}) {
    if (loaded.value && !force) return;
    loading.value = true;
    error.value = null;
    try {
      // An array, always. This store used to hold its own theory about which
      // envelope the liked-lists route sends, and the theory before that one was
      // wrong in a way that cost real behavior: destructuring `.documents` off
      // a bare array silently yields undefined -> [], so the store loaded ZERO
      // categories on every call — `isLiked` was always false, and
      // `ensureDefaultLiked`, seeing no default, created another built-in
      // category on every page load. Which envelope an endpoint uses is now the client's
      // business and no store's.
      const documents = await autoAuctionApiSdk.getLikedLists();
      categories.value = documents.map(normalizeCategory).filter(Boolean);
      loaded.value = true;
      // The built-in category must always exist — it backs the
      // dedicated page and the Live liked filter. Create lazily.
      ensureDefaultLiked().catch(() => {});
      // Pre-hydrate every referenced asset so the Saved tab
      // doesn't flash empty cards while individual lookups resolve.
      const allIds = Array.from(
        new Set(categories.value.flatMap((c) => c.assets.map((v) => v.id)))
      );
      await Promise.all(allIds.map((id) => ensureAsset(id)));
    } catch (e) {
      error.value = e;
      console.error('Failed to load liked lists', e);
    } finally {
      loading.value = false;
    }
  }

  // Merge externally-sourced asset cards into the bank so categories that
  // reference them hydrate without waiting on a per-id fetch. The bidding store
  // calls it with the lots it has just loaded. Only fills ids the bank doesn't
  // already hold, so a richer server doc is never clobbered by a stub.
  function registerAssets(cards) {
    const next = { ...assetBank.value };
    let changed = false;
    for (const card of cards ?? []) {
      const v = normalizeAssetCard(card);
      if (v && !next[v.id]) {
        next[v.id] = v;
        changed = true;
      }
    }
    if (changed) assetBank.value = next;
  }

  async function ensureAsset(assetId) {
    if (!assetId) return null;
    if (assetBank.value[assetId]) return assetBank.value[assetId];
    if (inflightAssetFetches.has(assetId)) {
      return inflightAssetFetches.get(assetId);
    }
    const promise = autoAuctionApiSdk
      .getAssetById(assetId)
      .then((doc) => {
        const v = normalizeVehicleDoc(doc);
        if (v) assetBank.value = { ...assetBank.value, [assetId]: v };
        inflightAssetFetches.delete(assetId);
        return v;
      })
      .catch((e) => {
        inflightAssetFetches.delete(assetId);
        console.warn('Failed to fetch asset', assetId, e);
        return null;
      });
    inflightAssetFetches.set(assetId, promise);
    return promise;
  }

  // Resolve a category's stored asset refs into hydrated records the UI
  // can render. Notes live on the membership so two categories can carry
  // different notes for the same asset.
  const hydrateCategory = (category) => {
    if (!category) return null;
    const assets = category.assets
      .map((entry) => {
        const v = assetBank.value[entry.id];
        if (!v) return null;
        return { ...v, note: entry.note };
      })
      .filter(Boolean);

    const estTotal = assets.reduce((sum, v) => sum + (v.price || 0), 0);
    const closingSoonCount = assets.filter((v) => v.auctionInHrs != null && v.auctionInHrs <= 48).length;

    return {
      ...category,
      assets,
      counts: {
        assets: assets.length,
        estTotal,
        estTotalLabel: estTotal ? `~${fmtPrice(estTotal)}` : '—',
        closingSoon: closingSoonCount
      }
    };
  };

  const hydratedCategories = computed(() =>
    categories.value.map(hydrateCategory).filter(Boolean)
  );

  const getCategory = (id) => hydratedCategories.value.find((c) => c.id === id) ?? null;

  // Apply the local sort on top of the hydrated list. Server payload never
  // sees this knob.
  function applySort(list) {
    const out = list.slice();
    if (sortMode.value === 'alphabetical') {
      out.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortMode.value === 'most-assets') {
      out.sort((a, b) => b.counts.assets - a.counts.assets);
    } else {
      // 'recent' — fall back to insertion order when timestamps are missing.
      out.sort((a, b) => {
        const aT = a.updatedAt || a.createdAt || '';
        const bT = b.updatedAt || b.createdAt || '';
        return bT.localeCompare(aT);
      });
    }
    return out;
  }

  const ownedCategories = computed(() => applySort(hydratedCategories.value));

  // The hero card surfaces the single most-urgent lot — what's auctioning
  // soonest across the categories.
  const closingSoonHero = computed(() => {
    let best = null;
    for (const category of hydratedCategories.value) {
      for (const asset of category.assets) {
        if (asset.auctionInHrs == null) continue;
        if (!best || asset.auctionInHrs < best.asset.auctionInHrs) {
          best = { asset, category };
        }
      }
    }
    return best;
  });

  const totalAssets = computed(() =>
    hydratedCategories.value.reduce((sum, c) => sum + c.counts.assets, 0)
  );

  const setSortMode = (mode) => {
    if (['recent', 'alphabetical', 'most-assets'].includes(mode)) {
      sortMode.value = mode;
    }
  };

  const addCategory = async (input) => {
    const body = {
      name: input.name ?? 'New category',
      active: true,
      description: input.description ?? '',
      target: input.target ?? null,
      assets: []
    };
    // Only the built-in category carries one, and it must reach the server:
    // this body is a fixed shape that drops anything not listed, which is why
    // `is_default` never survived and the name became the de-facto key.
    if (input.slug) body.slug = input.slug;
    try {
      const saved = await autoAuctionApiSdk.createLikedList(body);
      const normalized = normalizeCategory(saved);
      if (normalized) categories.value.push(normalized);
      return normalized?.id ?? null;
    } catch (e) {
      console.error('Failed to create category', e);
      return null;
    }
  };

  // ── The built-in category ────────────────────────────────────────────────
  // A single well-known list that any asset can be added to regardless of
  // which other lists it lives in. It's asset-centric (urgency-sorted on the
  // page) and feeds the Live liked filter.
  //
  // It is addressed by SLUG. It used to be found by its display name, which
  // made the name load-bearing: translate it or retitle it and the store found
  // nothing, `ensureDefaultLiked` minted a fresh empty list, and every liked
  // asset silently vanished from the Bid Stack, the Live filter, the dashboard
  // and the page — with no error anywhere.
  const LIKED_SLUG = 'liked';
  // The label stored on the built-in list when the app provisions it. It is
  // a fallback only: every surface renders this list from copy keyed off the
  // slug, so the stored string is not what a dealer reads and translating the
  // copy no longer moves anything.
  const LIKED_NAME = 'Liked';
  // What the built-in list was called before it had a slug. Read on the way in;
  // never written. The Like rename's own retired words are not read at all —
  // renameToLikedLists.mjs rewrites every stored one.
  const LEGACY_LIKED_NAME = 'My Vehicles';

  // The raw (un-hydrated) built-in category, if it's already loaded. The
  // fallbacks are for documents written before the slug existed: `is_built_in`
  // came from the server's own flag, and the legacy name only from the app.
  // Nothing seeds a liked list — `ensureDefaultLiked` below mints the built-in
  // one on the first like, with the slug on it — so after the migration the
  // first branch answers for every document and the other two are history.
  const defaultLikedRaw = computed(() =>
    categories.value.find((c) => c.slug === LIKED_SLUG) ??
    categories.value.find((c) => c.isBuiltIn) ??
    categories.value.find((c) => c.name === LEGACY_LIKED_NAME) ??
    null
  );

  // The hydrated default category (assets resolved against the bank).
  const defaultLiked = computed(() => {
    const raw = defaultLikedRaw.value;
    return raw ? hydrateCategory(raw) : null;
  });

  // Set of assetIds currently on the built-in list — the Live store reads this
  // to mark lots as liked without coupling to the category shape.
  const likedAssetIds = computed(
    () => new Set((defaultLikedRaw.value?.assets ?? []).map((v) => String(v.id)))
  );

  // The built-in category's id. Callers that need to tell it apart from a
  // dealer's own categories read this rather than re-deriving it — the sheet
  // used to compare display names, a second copy of the key that knew nothing
  // about `slug` or `is_default`.
  const defaultLikedId = computed(() => defaultLikedRaw.value?.id ?? null);

  const isInDefaultLiked = (assetId) =>
    assetId != null && likedAssetIds.value.has(String(assetId));

  // Find-or-create the default list. Awaitable so callers can rely on an id
  // being present before they add to it. Guards against a double-create race
  // by reusing an in-flight promise.
  let ensureDefaultLikedPromise = null;
  async function ensureDefaultLiked() {
    if (defaultLikedRaw.value) return defaultLikedRaw.value.id;
    // Never create before we know what already exists. defaultLikedRaw reads
    // the LOADED categories, so a caller that runs before (or races) loadCategories
    // sees null and mints a duplicate built-in category — one per click. Callers on a
    // cold page can't be relied on to await the load first, so the guard belongs
    // here. No recursion: loadCategories sets `loaded` before it calls back into
    // this, so the nested call short-circuits.
    if (!loaded.value) await loadCategories();
    if (defaultLikedRaw.value) return defaultLikedRaw.value.id;
    if (ensureDefaultLikedPromise) return ensureDefaultLikedPromise;
    ensureDefaultLikedPromise = addCategory({ name: LIKED_NAME, slug: LIKED_SLUG })
      .then((id) => {
        ensureDefaultLikedPromise = null;
        return id ?? defaultLikedRaw.value?.id ?? null;
      })
      .catch((e) => {
        ensureDefaultLikedPromise = null;
        console.error('Failed to ensure the built-in category', e);
        return null;
      });
    return ensureDefaultLikedPromise;
  }

  // Add any asset to the built-in list (independent of other lists). Returns
  // false if the list can't be resolved or the asset is already in it.
  async function addToLiked(asset) {
    const id = await ensureDefaultLiked();
    if (!id) return false;
    return addAssetToCategory(id, asset);
  }

  async function removeFromLiked(assetId) {
    const id = defaultLikedRaw.value?.id;
    if (!id) return;
    await removeAssetFromCategory(id, assetId);
  }

  // The per-item DELETE endpoint enforces access on the server — we just hit
  // it and trust the response. Bank update isn't reset on failure: the row
  // stays gone optimistically for the rare error, and the next
  // loadCategories refresh repairs it.
  const removeAssetFromCategory = async (categoryId, assetId) => {
    const category = categories.value.find((c) => c.id === categoryId);
    if (!category) return;
    const previous = category.assets;
    category.assets = category.assets.filter((v) => v.id !== assetId);
    try {
      const saved = await autoAuctionApiSdk.removeAssetFromLikedList(categoryId, assetId);
      applyServerCollection(category, saved);
    } catch (e) {
      category.assets = previous;
      console.error('Failed to remove asset from category', e);
    }
  };

  // Adds route through the dedicated POST endpoint so the server can stamp
  // any per-membership metadata from the JWT.
  const addAssetToCategory = async (categoryId, asset, note = null, extra = {}) => {
    const normalized = normalizeAssetCard(asset);
    if (!normalized) return false;
    const category = categories.value.find((c) => c.id === categoryId);
    if (!category) return false;
    if (category.assets.some((v) => v.id === normalized.id)) return false;

    const targetPrice = extra.targetPrice ?? null;
    const items = extra.items ?? [];
    // Register/refresh the asset in the bank — newer payloads can win
    // over a stale seed if the API hands us better data later.
    assetBank.value = { ...assetBank.value, [normalized.id]: normalized };
    const previous = category.assets;
    category.assets = [
      ...category.assets,
      { id: normalized.id, note, targetPrice, items },
    ];
    try {
      const saved = await autoAuctionApiSdk.addAssetToLikedList(
        categoryId,
        normalized.id,
        note,
        { targetPrice, items }
      );
      applyServerCollection(category, saved);
      return true;
    } catch (e) {
      category.assets = previous;
      console.error('Failed to add asset to category', e);
      return false;
    }
  };

  // Adopt the server-side truth onto the local row after a write so the
  // optimistic state (which may have estimated timestamps) converges.
  // Targeted re-map keeps the reactive shape intact — wholesale replacement
  // would force every consumer to re-render.
  function applyServerCollection(category, savedDoc) {
    if (!savedDoc) return;
    if (savedDoc.updated_at) category.updatedAt = savedDoc.updated_at;
    if (Array.isArray(savedDoc.assets)) {
      category.assets = savedDoc.assets.map((v) => ({
        id: v.assetId,
        note: v.note ?? null,
        targetPrice: v.targetPrice ?? null,
        items: v.items ?? []
      }));
    }
  }

  // Helper that builds the full replace body the PUT endpoint expects.
  // Used for rename / note / worksheet edits — anything beyond a single
  // add/remove.
  async function persistCollection(category) {
    const saved = await autoAuctionApiSdk.updateLikedList(category.id, {
      name: category.name,
      active: true,
      description: category.description ?? '',
      target: category.target ?? null,
      assets: category.assets.map((v) => ({
        assetId: v.id,
        note: v.note,
        // Echo worksheet fields so a rename / note edit doesn't strip them.
        ...(v.targetPrice != null ? { targetPrice: v.targetPrice } : {}),
        ...(Array.isArray(v.items) && v.items.length ? { items: v.items } : {})
      }))
    });
    // Keep the local `updatedAt` in sync so "recent" sort reflects this
    // write the next time the computed runs.
    if (saved?.updated_at) category.updatedAt = saved.updated_at;
  }

  // Inline-edit the note on a saved entry. Optimistic update first so
  // the textarea doesn't appear to revert mid-typing; rollback on failure.
  const updateAssetNote = async (categoryId, assetId, note) => {
    const category = categories.value.find((c) => c.id === categoryId);
    if (!category) return false;
    const entry = category.assets.find((v) => v.id === assetId);
    if (!entry) return false;
    const previous = entry.note;
    if (previous === note) return true;
    entry.note = note;
    try {
      await persistCollection(category);
      return true;
    } catch (e) {
      entry.note = previous;
      console.error('Failed to update saved note', e);
      return false;
    }
  };

  // ── Dealer planning worksheet ────────────────────────────────────────────
  // The worksheet (target bid + itemized build-out + note) is canonicalized
  // on the built-in category so a asset carries one set of
  // planning numbers regardless of which other lists it lives in. Build-out
  // is the sum of `items[].cost` — the breakdown itself is what persists.

  // Read the current worksheet for a asset from its built-in list entry.
  // Reads raw categories so it works before asset hydration. Returns
  // empty/null fields when the asset isn't on the list yet.
  const worksheetForAsset = (assetId) => {
    const entry = (defaultLikedRaw.value?.assets ?? []).find(
      (v) => String(v.id) === String(assetId)
    );
    return {
      note: entry?.note ?? null,
      targetPrice: entry?.targetPrice ?? null,
      items: entry?.items ?? []
    };
  };

  // Patch worksheet fields on an existing membership entry. Optimistic, with
  // rollback — mirrors updateAssetNote but covers all three fields.
  const updateAssetWorksheet = async (categoryId, assetId, patch) => {
    const category = categories.value.find((c) => c.id === categoryId);
    if (!category) return false;
    const entry = category.assets.find((v) => v.id === assetId);
    if (!entry) return false;
    const previous = { note: entry.note, targetPrice: entry.targetPrice, items: entry.items };
    if (patch.note !== undefined) entry.note = patch.note;
    if (patch.targetPrice !== undefined) entry.targetPrice = patch.targetPrice;
    if (patch.items !== undefined) entry.items = patch.items;
    try {
      await persistCollection(category);
      return true;
    } catch (e) {
      Object.assign(entry, previous);
      console.error('Failed to update planning worksheet', e);
      return false;
    }
  };

  // Save an asset's worksheet to the built-in liked list — updating the entry
  // in place if it's already there, otherwise adding it with the worksheet
  // values in a single round-trip. Used by the VDP planning worksheet. A
  // worksheet is still SAVED — persisting numbers is a save, not a like.
  const saveWorksheetToLiked = async (asset, patch) => {
    const categoryId = await ensureDefaultLiked();
    if (!categoryId) return false;
    const assetId = asset?.id ?? idOf(asset);
    if (!assetId) return false;
    if (isInDefaultLiked(assetId)) {
      return updateAssetWorksheet(categoryId, assetId, patch);
    }
    return addAssetToCategory(categoryId, asset, patch.note ?? null, {
      targetPrice: patch.targetPrice ?? null,
      items: patch.items ?? []
    });
  };

  const renameCategory = async (categoryId, name) => {
    const category = categories.value.find((c) => c.id === categoryId);
    if (!category) return false;
    const trimmed = (name ?? '').trim();
    if (!trimmed || category.name === trimmed) return category.name === trimmed;
    const previous = category.name;
    category.name = trimmed;
    try {
      await persistCollection(category);
      return true;
    } catch (e) {
      category.name = previous;
      console.error('Failed to rename category', e);
      return false;
    }
  };

  const archiveCategory = async (categoryId) => {
    const idx = categories.value.findIndex((c) => c.id === categoryId);
    if (idx === -1) return false;
    const removed = categories.value[idx];
    categories.value.splice(idx, 1);
    try {
      await autoAuctionApiSdk.archiveLikedList(categoryId);
      return true;
    } catch (e) {
      categories.value.splice(idx, 0, removed);
      console.error('Failed to archive category', e);
      return false;
    }
  };

  // Read-only: which category ids currently include this asset. The
  // heart icon and the save sheet both lean on this.
  const categoryIdsForAsset = (assetId) => {
    if (!assetId) return [];
    return categories.value
      .filter((c) => c.assets.some((v) => v.id === assetId))
      .map((c) => c.id);
  };

  // Liked means the asset is on ANY of the dealer's lists. The live truth —
  // what every heart reads, so a tap flips the heart on the spot.
  const isLiked = (assetId) => categoryIdsForAsset(assetId).length > 0;

  // Unliking, everywhere at once: the asset leaves EVERY list it is in.
  //
  // That is what the heart means in this app. It reports one state per asset
  // (`isLiked`), so a heart that only emptied the built-in list would come back
  // filled the moment the page re-read it, and the dealer would be told the
  // asset is still liked by the control they just used to unlike it.
  // The asset detail page's heart already walked the lists by hand; this is
  // that walk, owned by the store, so the Bid Stack's swipe tray and anything
  // else that unlikes an asset cannot grow a second version of it.
  //
  // Answers whether the asset is actually gone. Each delete rolls its own
  // list back when the server refuses it (see removeAssetFromCategory), so
  // a refusal leaves the asset liked and this returns false.
  async function unlikeEverywhere(assetId) {
    const id = String(assetId);
    // Snapshot before the walk — removeAssetFromCategory mutates the category's
    // assets synchronously, so iterating the live list would skip entries.
    const categoryIds = categoryIdsForAsset(id);
    // One at a time, and awaited: a rolled-back delete has to be visible in the
    // answer below rather than racing it.
    for (const categoryId of categoryIds) {
      await removeAssetFromCategory(categoryId, id);
    }
    return !isLiked(id);
  }

  // ── Pending unlikes ────────────────────────────────────────────
  // An unlike must never pull a row out from under the dealer's thumb. Only the
  // Liked route drops a row on the tap, because that route IS the list; on every
  // other surface the row stays and the heart empties until the dealer leaves.
  //
  // So the built-in list has two readings. `defaultLiked` is the server truth,
  // which `isLiked` and every heart read and which the Liked route renders.
  // `displayLiked` is that truth with this session's unliked rows put back
  // where they were, and it is what every other surface renders. Nothing here
  // is persisted, so a reload starts from the server truth.
  //
  // Keyed by asset id; each value is the entries the asset lost, each tagged
  // with the list it left and the index it left from.
  const pendingUnliked = reactive(new Map());

  // Unlike an asset and remember what left, so the display lists hold the row.
  async function unlike(assetId) {
    const id = String(assetId);
    // Snapshot before the walk: removeAssetFromCategory empties each list
    // synchronously, so afterwards there is nothing left to read.
    const before = categoryIdsForAsset(id).map((categoryId) => {
      const category = categories.value.find((c) => c.id === categoryId);
      const index = category.assets.findIndex((v) => String(v.id) === id);
      return { categoryId, index, entry: { ...category.assets[index] } };
    });
    await unlikeEverywhere(id);
    // Only the deletes that landed. A refused delete rolls back, so the asset
    // is still on that list, and a pending entry beside the live one would draw
    // the row twice.
    const landed = before.filter(({ categoryId }) => {
      const category = categories.value.find((c) => c.id === categoryId);
      return !category || !category.assets.some((v) => String(v.id) === id);
    });
    if (landed.length) pendingUnliked.set(id, landed);
    return !isLiked(id);
  }

  // Like an asset: it goes on the built-in list, which is where every heart in
  // the app likes to. Any pending unlike for it is dropped first, or the
  // display list would hold a stale copy beside the row the add is about to put
  // back, and the dealer would see the same car twice.
  async function like(asset) {
    const id = asset?.id ?? idOf(asset);
    if (id != null) pendingUnliked.delete(String(id));
    return addToLiked(asset);
  }

  // The raw built-in list as every surface off the Liked route sees it.
  const displayLikedRaw = computed(() => {
    const raw = defaultLikedRaw.value;
    if (!raw) return null;
    const held = [];
    for (const entries of pendingUnliked.values()) {
      for (const entry of entries) {
        if (entry.categoryId === raw.id) held.push(entry);
      }
    }
    if (!held.length) return raw;
    const assets = raw.assets.slice();
    // Ascending by index, so each held row lands back where it left from.
    for (const held_entry of held.slice().sort((a, b) => a.index - b.index)) {
      const { index, entry } = held_entry;
      // Deduplicate by id: a re-like inside the session puts the live row back,
      // and the held copy has to give way to it rather than double it.
      if (assets.some((v) => String(v.id) === String(entry.id))) continue;
      assets.splice(Math.min(Math.max(index, 0), assets.length), 0, entry);
    }
    return { ...raw, assets };
  });

  // The hydrated display list, shaped exactly like `defaultLiked` — same
  // assets, same counts — so a surface swaps one for the other and reads the
  // counts off it.
  const displayLiked = computed(() => {
    const raw = displayLikedRaw.value;
    return raw ? hydrateCategory(raw) : null;
  });

  const displayLikedAssetIds = computed(
    () => new Set((displayLikedRaw.value?.assets ?? []).map((v) => String(v.id)))
  );

  // Whether a row for this asset belongs on a display list. This is not the
  // heart's answer — the heart reads `isLiked`, which is live — it is "is there
  // a row", which stays true through a pending unlike.
  const isLikedForDisplay = (assetId) =>
    assetId != null && displayLikedAssetIds.value.has(String(assetId));

  // Let the display lists catch up with the server truth. The app calls this on
  // every route change: once the dealer has left the page, nothing is under
  // their thumb and a held row has done its job.
  function commitPendingUnlikes() {
    pendingUnliked.clear();
  }

  // Read-only: every note attached to this asset across the user's
  // categories, tagged with the category it lives in. Notes no longer
  // render on the category rows — the asset detail page surfaces them
  // instead. Reads raw categories so it works before (and independent of)
  // asset hydration.
  const notesForAsset = (assetId) => {
    if (!assetId) return [];
    const out = [];
    for (const c of categories.value) {
      const entry = c.assets.find((v) => v.id === assetId);
      if (entry?.note) {
        out.push({
          categoryId: c.id,
          collectionName: c.name,
          note: entry.note
        });
      }
    }
    return out;
  };

  return {
    categories,
    hydratedCategories,
    ownedCategories,
    sortMode,
    loading,
    error,
    loaded,
    loadCategories,
    ensureAsset,
    registerAssets,
    assetBank,
    totalAssets,
    closingSoonHero,
    getCategory,
    addCategory,
    addAssetToCategory,
    removeAssetFromCategory,
    defaultLiked,
    defaultLikedId,
    likedAssetIds,
    isInDefaultLiked,
    ensureDefaultLiked,
    addToLiked,
    removeFromLiked,
    updateAssetNote,
    worksheetForAsset,
    updateAssetWorksheet,
    saveWorksheetToLiked,
    renameCategory,
    archiveCategory,
    categoryIdsForAsset,
    // Liked: the one word for the dealer's mark on an asset.
    like,
    unlike,
    isLiked,
    unlikeEverywhere,
    pendingUnliked,
    displayLiked,
    displayLikedAssetIds,
    isLikedForDisplay,
    commitPendingUnlikes,
    notesForAsset,
    setSortMode
  };
});
